import React from 'react'
import {
	Control,
	FieldValues,
	RegisterOptions,
	useController,
} from 'react-hook-form'

import BaseTextField from 'components/Form/BaseTextField'
import type { BaseTextFieldProps } from 'components/Form/BaseTextField'

export const TEST_ID = 'default-test-id-form-text-field'

export type Props = {
	control: Control<FieldValues>
	'data-testid'?: string
	disabled?: boolean
	label?: string
	name: string
	readOnly?: boolean
	required?: boolean
	rules?: RegisterOptions<FieldValues>
} & Omit<BaseTextFieldProps, 'value' | 'ref'>

const FormTextField = ({
	name,
	label,
	helperText,
	control,
	required,
	disabled,
	readOnly,
	rules = {},
	'data-testid': dataTestid,
	...props
}: Props) => {
	const {
		field: { value, onChange, onBlur, ...fieldProps },
		fieldState: { error, isTouched },
	} = useController({
		name,
		control,
		rules: {
			required:
				(required && {
					value: true,
					message: 'This field is required',
				}) ||
				undefined,
			...rules,
		},
		disabled,
		defaultValue: '',
	})

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const trimmedValue = e.target?.value?.trim()
		onChange(trimmedValue)
		onBlur()
	}

	return (
		<BaseTextField
			label={label}
			helperText={
				(!readOnly && error && isTouched ? error.message : null) ||
				helperText
			}
			error={!readOnly && error && isTouched}
			readOnly={readOnly}
			onBlur={handleBlur}
			onChange={onChange}
			{...fieldProps}
			{...props}
			value={value}
			data-testid={dataTestid || TEST_ID}
			sx={{
				'& .MuiInputLabel-root': {
					fontWeight: 'normal', // Set the fontWeight to normal for the label
				},
			}}
		/>
	)
}

export default FormTextField
